import { useState, useEffect } from 'react';

/**
 * useOpsyName (v4.9) — the tenant's display name for the OpSy assistant.
 *
 * Tenants can rename the assistant (Settings → General → Assistant name);
 * every user-visible "OpSy" string in the app renders through this hook.
 * Identifiers, routes, testids, and internal protocol markers never change.
 *
 * Resolution mirrors usePortalConfig: customer session tenant → admin user
 * tenant → hostname subdomain → ?tenant= override. Fetched once per page
 * load (module-level cache shared by every component).
 */

export const DEFAULT_OPSY_NAME = 'OpSy';

// Cache keyed by storeSlug ('' = tenant-level) — v4.9.1 store-aware names
// let store A greet as "Dave" while store B greets as "Princess".
const _cachedNames = {};
const _inflight = {};

const buildUrl = (storeSlug) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  // Tenant identity params are tracked SEPARATELY from the store param —
  // store_slug must never satisfy the "already have tenant identity" checks.
  const tenantParams = [];
  try {
    const session = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    const tenantId = session?.tenant?.id || session?.tenant_id;
    if (tenantId) tenantParams.push(`tenant_id=${encodeURIComponent(tenantId)}`);
    else {
      // v4.9.2 — sessions also carry the tenant subdomain
      const sub = session?.tenant_subdomain || session?.tenant?.subdomain;
      if (sub) tenantParams.push(`subdomain=${encodeURIComponent(sub)}`);
    }
  } catch { /* noop */ }
  if (!tenantParams.length) {
    try {
      const user = JSON.parse(localStorage.getItem('plateos_user') || '{}');
      if (user?.tenant_id) tenantParams.push(`tenant_id=${encodeURIComponent(user.tenant_id)}`);
    } catch { /* noop */ }
  }
  if (!tenantParams.length) {
    try {
      const host = window.location.hostname || '';
      const parts = host.split('.');
      const sub = parts.length > 2 ? parts[0] : '';
      if (sub && sub !== 'www' && sub !== 'localhost') {
        tenantParams.push(`subdomain=${encodeURIComponent(sub)}`);
      }
      const qp = new URLSearchParams(window.location.search);
      const tParam = qp.get('tenant');
      if (tParam) tenantParams.push(`subdomain=${encodeURIComponent(tParam)}`);
    } catch { /* noop */ }
  }
  const params = [...tenantParams];
  if (storeSlug) params.push(`store_slug=${encodeURIComponent(storeSlug)}`);
  return `${base}/api/settings/public${params.length ? `?${params.join('&')}` : ''}`;
};

export const fetchOpsyName = (storeSlug = '') => {
  const key = storeSlug || '';
  if (_cachedNames[key]) return Promise.resolve(_cachedNames[key]);
  if (_inflight[key]) return _inflight[key];
  _inflight[key] = (async () => {
    try {
      const res = await fetch(buildUrl(storeSlug));
      if (res.ok) {
        const data = await res.json();
        const name = (data?.opsy_display_name || '').trim();
        _cachedNames[key] = name || DEFAULT_OPSY_NAME;
      } else {
        _cachedNames[key] = DEFAULT_OPSY_NAME;
      }
    } catch {
      _cachedNames[key] = DEFAULT_OPSY_NAME;
    } finally {
      delete _inflight[key];
    }
    return _cachedNames[key];
  })();
  return _inflight[key];
};

const useOpsyName = (storeSlug = '') => {
  const key = storeSlug || '';
  const [name, setName] = useState(_cachedNames[key] || DEFAULT_OPSY_NAME);

  useEffect(() => {
    let mounted = true;
    fetchOpsyName(key).then((n) => { if (mounted && n) setName(n); });
    return () => { mounted = false; };
  }, [key]);

  return name;
};

export default useOpsyName;
